import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useVariant } from '../contexts/VariantContext';
import { useSEO } from '../contexts/SEOContext';
import { useDomainManager } from '../config';

const Contact = () => {
  const { brand } = useTheme();
  const { styles } = useVariant();
  const { currentSEO } = useSEO();
  const domainManager = useDomainManager(brand?.id);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    alert('Thank you for your message! We will get back to you soon.');
  };

  // Get domain-specific contact content using the unified configuration
  const getContactContent = () => {
    // Try to get content from domain config first
    const domainConfig = domainManager.getAllSettings();
    if (domainConfig.contact) {
      return domainConfig.contact;
    }
    
    // Fallback to legacy system
    const domainId = brand?.id;
    
    switch (domainId) {

      case 'geotab-dashcam':
      case 'geotab-dashcams':
        return {
          title: 'Contact Geotab DashCams',
          subtitle: 'Professional dash cam solutions and support',
          description: 'Need help choosing the perfect dash cam for your fleet? Our specialists are ready to assist you with product selection, installation, and ongoing support.',
          contactInfo: {
            phone: '+1 (555) 234-5678',
            email: 'info@geotabdashcams.com',
            address: '456 Dash Cam Boulevard, Safety Town, ST 23456',
            hours: 'Monday - Friday: 7:00 AM - 7:00 PM EST'
          },
          departments: [
            {
              name: 'Product Sales',
              description: 'Dash cam selection and pricing',
              contact: 'sales@geotabdashcams.com',
              icon: '📷'
            },
            {
              name: 'Technical Support',
              description: 'Installation and troubleshooting',
              contact: 'tech@geotabdashcams.com',
              icon: '🔧'
            },
            {
              name: 'Fleet Services',
              description: 'Bulk orders and fleet management',
              contact: 'fleet@geotabdashcams.com',
              icon: '🚛'
            }
          ]
        };

      case 'fleetkeyless':
        return {
          title: 'Contact Fleet Keyless',
          subtitle: 'Revolutionary keyless fleet management',
          description: 'Transform your fleet with our advanced keyless access control systems. Contact us to learn how we can enhance your fleet security and efficiency.',
          contactInfo: {
            phone: '+1 (555) 345-6789',
            email: 'hello@fleetkeyless.com',
            address: '789 Keyless Avenue, Innovation District, ID 34567',
            hours: 'Monday - Friday: 9:00 AM - 5:00 PM EST'
          },
          departments: [
            {
              name: 'Solutions',
              description: 'Keyless system consultation',
              contact: 'solutions@fleetkeyless.com',
              icon: '🔑'
            },
            {
              name: 'Integration',
              description: 'System integration and setup',
              contact: 'integration@fleetkeyless.com',
              icon: '🔗'
            },
            {
              name: 'Security',
              description: 'Security and compliance support',
              contact: 'security@fleetkeyless.com',
              icon: '🛡️'
            }
          ]
        };

      case 'fleethoursofservice':
        return {
          title: 'Contact Fleet Hours of Service',
          subtitle: 'ELD compliance and driver safety experts',
          description: 'Ensure DOT compliance with our comprehensive ELD solutions. Our compliance experts are here to help you navigate regulations and implement effective driver safety programs.',
          contactInfo: {
            phone: '+1 (555) 456-7890',
            email: 'compliance@fleethoursofservice.com',
            address: '321 Compliance Center, Safety Plaza, SP 45678',
            hours: 'Monday - Friday: 8:00 AM - 6:00 PM EST'
          },
          departments: [
            {
              name: 'Compliance',
              description: 'ELD compliance and regulations',
              contact: 'compliance@fleethoursofservice.com',
              icon: '📋'
            },
            {
              name: 'Driver Safety',
              description: 'Safety programs and training',
              contact: 'safety@fleethoursofservice.com',
              icon: '🛡️'
            },
            {
              name: 'Implementation',
              description: 'ELD system setup and training',
              contact: 'setup@fleethoursofservice.com',
              icon: '⚙️'
            }
          ]
        };

      case 'smarter-tracking':
        return {
          title: 'Contact Smarter Tracking',
          subtitle: 'Intelligent fleet management solutions',
          description: 'Ready to optimize your fleet operations with AI-powered tracking and analytics? Our team of experts is here to help you implement the perfect solution.',
          contactInfo: {
            phone: '+1 (555) 567-8901',
            email: 'contact@smartertracking.com',
            address: '654 Smart Street, Innovation Hub, IH 56789',
            hours: 'Monday - Friday: 8:00 AM - 6:00 PM EST'
          },
          departments: [
            {
              name: 'Sales',
              description: 'Solution consultation and pricing',
              contact: 'sales@smartertracking.com',
              icon: '💼'
            },
            {
              name: 'Analytics',
              description: 'Data insights and optimization',
              contact: 'analytics@smartertracking.com',
              icon: '📊'
            },
            {
              name: 'Support',
              description: 'Technical support and training',
              contact: 'support@smartertracking.com',
              icon: '🛠️'
            }
          ]
        };

      default:
        return {
          title: 'Contact Us',
          subtitle: 'Get in touch with our experts',
          description: 'Have questions about our solutions? Our team of experts is ready to help you find the perfect solution for your business needs.',
          contactInfo: {
            phone: '+1 (555) 000-0000',
            email: 'info@company.com',
            address: '123 Business Street, City, State 12345',
            hours: 'Monday - Friday: 9:00 AM - 5:00 PM EST'
          },
          departments: [
            {
              name: 'General Inquiries',
              description: 'General questions and information',
              contact: 'info@company.com',
              icon: '📧'
            },
            {
              name: 'Sales',
              description: 'Product information and pricing',
              contact: 'sales@company.com',
              icon: '💼'
            },
            {
              name: 'Support',
              description: 'Technical support and assistance',
              contact: 'support@company.com',
              icon: '🛠️'
            }
          ]
        };
    }
  };

  const { title, subtitle, description, contactInfo } = getContactContent();

  return (
    <div className={`pt-16 ${styles.container}`}>
      {/* Hero Section */}
      <div className={`relative overflow-hidden ${styles.hero}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className={`text-4xl md:text-6xl font-bold mb-6 ${styles.text}`}>
              {title}
            </h1>
            <p className={`text-xl md:text-2xl mb-4 ${styles.textSecondary}`}>
              {subtitle}
            </p>
            <p className={`text-lg max-w-3xl mx-auto ${styles.textSecondary}`}>
              {description}
            </p>
          </div>
        </div>
        
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
      </div>

      {/* Contact Form and Info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className={`rounded-2xl shadow-lg p-8 ${styles.card}`}>
            <h2 className={`text-2xl font-bold mb-6 ${styles.accent}`}>
              Send us a Message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${styles.input}`}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${styles.input}`}
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${styles.input}`}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${styles.input}`}
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${styles.input}`}
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${styles.input}`}
                  placeholder="Tell us about your needs..."
                ></textarea>
              </div>
              
              <button
                type="submit"
                className={`w-full px-6 py-4 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity duration-300 ${styles.button}`}
              >
                Send Message
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Contact Details */}
            <div className={`rounded-2xl shadow-lg p-8 ${styles.card}`}>
              <h2 className={`text-2xl font-bold mb-6 ${styles.accent}`}>
                Get in Touch
              </h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${styles.accent} bg-opacity-20`}>
                    <span className="text-xl">📞</span>
                  </div>
                  <div>
                    <h3 className={`font-semibold ${styles.text}`}>Phone</h3>
                    <p className={styles.textSecondary}>{contactInfo.phone}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${styles.accent} bg-opacity-20`}>
                    <span className="text-xl">📧</span>
                  </div>
                  <div>
                    <h3 className={`font-semibold ${styles.text}`}>Email</h3>
                    <p className={styles.textSecondary}>{contactInfo.email}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${styles.accent} bg-opacity-20`}>
                    <span className="text-xl">📍</span>
                  </div>
                  <div>
                    <h3 className={`font-semibold ${styles.text}`}>Address</h3>
                    <p className={styles.textSecondary}>{contactInfo.address}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${styles.accent} bg-opacity-20`}>
                    <span className="text-xl">🕒</span>
                  </div>
                  <div>
                    <h3 className={`font-semibold ${styles.text}`}>Business Hours</h3>
                    <p className={styles.textSecondary}>{contactInfo.hours}</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

    </div>
  );
};

export default Contact; 